import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { Badge } from "@/dentlab/src/components/ui/badge";
import { cn } from "@/dentlab/src/lib/utils/cn";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from "@/dentlab/src/components/ui/alert-dialog";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";

export interface ActiveDelivery {
	delivery_id: number;
	job_id: number;
	job_code: string | null;
	job_title: string | null;
	status: string;
	courier_type: string | null;
	courier_company_name: string | null;
	description: string | null;
	scheduled_departure_at: string | null;
	created_at: string;
	vendor_org_id?: string;
	vendor_name?: string;
	client_id?: string | null;
}

interface DeliveriesInboxWidgetProps {
	deliveries: ActiveDelivery[];
	onDeliveryClick: (delivery: ActiveDelivery) => void;
	onMarkDelivered?: (deliveryId: number) => Promise<void>;
	vendorOrgId?: string;
	className?: string;
	deliveriesPageLink?: string;
}

const STATUS_STYLES: Record<string, string> = {
	pending: "bg-gray-100 text-gray-700 border-gray-200",
	in_transit: "bg-yellow-100 text-yellow-800 border-yellow-200",
};

const STATUS_LABELS: Record<string, string> = {
	pending: "Ausstehend",
	in_transit: "Unterwegs",
};

const formatDepartureTime = (date: string | null): string => {
	if (!date) return "";
	return new Date(date).toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const DeliveriesInboxWidget: React.FC<DeliveriesInboxWidgetProps> = ({
	deliveries,
	onDeliveryClick,
	onMarkDelivered,
	vendorOrgId,
	className,
	deliveriesPageLink,
}) => {
	const filtered = vendorOrgId
		? deliveries.filter((d) => d.vendor_org_id === vendorOrgId)
		: deliveries;
	const [confirmId, setConfirmId] = useState<number | null>(null);
	const [marking, setMarking] = useState(false);

	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col",
				className
			)}
			data-testid="deliveries-inbox-widget"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 flex-shrink-0">
				<Truck className="h-4 w-4 text-gray-500" />
				<h3 className="text-sm font-semibold text-gray-900">
					Lieferungen
				</h3>
				{filtered.length > 0 && (
					<Badge
						variant="secondary"
						className="h-5 min-w-[20px] px-1.5 text-xs"
					>
						{filtered.length}
					</Badge>
				)}
				{deliveriesPageLink && (
					<Link
						to={deliveriesPageLink}
						className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
						aria-label="Zur Lieferungen-Seite"
						data-testid="deliveries-page-link"
					>
						<span>Alle</span>
						<ArrowRight className="h-3 w-3" />
					</Link>
				)}
			</div>

			<div className="border-t border-gray-200" />

			{filtered.length === 0 ? (
				<div className="flex-1 flex items-center justify-center gap-2 text-gray-400">
					<Truck className="h-4 w-4 opacity-40" />
					<p className="text-xs text-gray-400">
						Keine aktiven Lieferungen
					</p>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto px-2 space-y-0.5">
					{filtered.map((delivery) => (
						<div
							key={delivery.delivery_id}
							className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-gray-50 cursor-pointer transition-colors"
							onClick={() => onDeliveryClick(delivery)}
							data-testid={`delivery-item-${delivery.delivery_id}`}
							aria-label={`Lieferung öffnen: ${delivery.job_code || ""} ${delivery.job_title || ""}`}
						>
							<Badge
								variant="outline"
								className={cn(
									"text-[11px] px-1.5 py-0 flex-shrink-0",
									STATUS_STYLES[delivery.status] || ""
								)}
							>
								{STATUS_LABELS[delivery.status] ||
									delivery.status}
							</Badge>
							<span className="text-[13px] text-gray-900 truncate flex-1 min-w-0">
								{delivery.job_code}
								{delivery.job_title
									? ` — ${delivery.job_title}`
									: ""}
							</span>
							{delivery.scheduled_departure_at && (
								<span className="text-[11px] text-gray-400 whitespace-nowrap flex items-center gap-0.5 flex-shrink-0">
									<Clock className="h-2.5 w-2.5" />
									{formatDepartureTime(
										delivery.scheduled_departure_at
									)}
								</span>
							)}
							{onMarkDelivered && (delivery.status === "pending" || delivery.status === "in_transit") && (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded text-green-600 hover:bg-green-50 transition-colors"
											onClick={(e) => {
												e.stopPropagation();
												setConfirmId(delivery.delivery_id);
											}}
											data-testid={`mark-delivered-${delivery.delivery_id}`}
											aria-label="Als zugestellt markieren"
										>
											<CheckCircle2 className="h-3.5 w-3.5" />
										</button>
									</TooltipTrigger>
									<TooltipContent side="left" className="text-xs">
										Als zugestellt markieren
									</TooltipContent>
								</Tooltip>
							)}
						</div>
					))}
				</div>
			)}
		{onMarkDelivered && (
				<AlertDialog open={confirmId !== null} onOpenChange={(open) => { if (!open) setConfirmId(null); }}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Lieferung als zugestellt markieren?</AlertDialogTitle>
							<AlertDialogDescription>
								Die Lieferung wird als zugestellt markiert und das Labor wird benachrichtigt.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={marking}>Abbrechen</AlertDialogCancel>
							<AlertDialogAction
								disabled={marking}
								onClick={async () => {
									if (confirmId === null) return;
									setMarking(true);
									await onMarkDelivered(confirmId);
									setMarking(false);
									setConfirmId(null);
								}}
							>
								{marking ? "Markiere..." : "Zugestellt"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			)}
		</div>
	);
};
